import { useEffect, useState } from "react";
import api, { formatError } from "../../lib/api";
import { toast } from "sonner";
import { Save, Loader2, Building2, Plus, Power, Link2, X } from "lucide-react";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);

function AddCollegeModal({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", code: "", admin_name: "", admin_email: "", plan: "starter" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name || !f.code || !f.admin_email) return toast.error("Name, code and admin email required");
    setBusy(true);
    try { const { data } = await api.post("/admin/colleges", f); toast.success(`College onboarded · admin password ${data.default_password}`); onDone(); onClose(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-lift w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4"><div className="font-heading font-bold text-lg text-ink">Onboard College</div><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button></div>
        <div className="space-y-3">
          <input data-testid="college-name" placeholder="College name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
          <input data-testid="college-code" placeholder="Code (e.g. MIT)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num uppercase focus:outline-none focus:ring-2 focus:ring-brand" />
          <input data-testid="college-admin-name" placeholder="Admin name" value={f.admin_name} onChange={(e) => setF({ ...f, admin_name: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
          <input data-testid="college-admin-email" placeholder="Admin email" value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
          <select value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"><option value="starter">Starter (100 credits)</option><option value="growth">Growth (500 credits)</option></select>
        </div>
        <button data-testid="save-college" onClick={save} disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full disabled:opacity-60 transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Onboard College"}</button>
      </div>
    </div>
  );
}

export default function AdminBilling() {
  const [cfg, setCfg] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(false);

  const load = () => { api.get("/admin/billing-config").then((r) => setCfg(r.data.config)); api.get("/admin/colleges").then((r) => setColleges(r.data)); };
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const setPlanField = (list, id, field, val) => setCfg({ ...cfg, [list]: cfg[list].map((p) => p.id === id ? { ...p, [field]: val } : p) });
  const setPayg = (k, v) => setCfg({ ...cfg, individual_plans: cfg.individual_plans.map((p) => p.id === "payg" ? { ...p, payg_prices: { ...p.payg_prices, [k]: parseInt(v || 0) } } : p) });

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/billing-config", { config: cfg }); toast.success("Pricing saved"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (t) => {
    const status = t.status === "active" ? "suspended" : "active";
    await api.put(`/admin/colleges/${t.id}/status`, { status });
    toast.success(`${t.name} ${status}`);
    load();
  };
  const paymentLink = async (t) => {
    const { data } = await api.post(`/admin/colleges/${t.id}/payment-link`, { kind: "college_growth" });
    await navigator.clipboard.writeText(data.link).catch(() => {});
    toast.success("Payment link copied to clipboard");
  };

  const pro = cfg.individual_plans.find((p) => p.id === "pro");
  const payg = cfg.individual_plans.find((p) => p.id === "payg");
  const cur = cfg.currency;

  return (
    <div className="space-y-6 max-w-5xl">
      {modal && <AddCollegeModal onClose={() => setModal(false)} onDone={load} />}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Commercialization</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Billing & Plans</h1>
          <p className="text-ink-soft text-sm mt-1">Configure pricing, credits and manage colleges. Nothing is hard-coded.</p>
        </div>
        <button data-testid="save-billing" onClick={save} disabled={busy} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full shadow-ambient transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Pricing</button>
      </div>

      {/* Pricing config */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4">Individual Pricing</div>
          <div className="space-y-3">
            <Row label="Pro (per month)"><Money cur={cur} testid="price-pro" value={pro.price} onChange={(v) => setPlanField("individual_plans", "pro", "price", parseInt(v || 0))} /></Row>
            <Row label="Pay Per — AI Interview"><Money cur={cur} testid="price-payg-ai" value={payg.payg_prices.ai} onChange={(v) => setPayg("ai", v)} /></Row>
            <Row label="Pay Per — Expert Interview"><Money cur={cur} testid="price-payg-expert" value={payg.payg_prices.expert} onChange={(v) => setPayg("expert", v)} /></Row>
            <Row label="Free AI quota (interviews)"><input data-testid="free-quota" type="number" value={cfg.free_ai_quota} onChange={(e) => setCfg({ ...cfg, free_ai_quota: parseInt(e.target.value || 0) })} className="w-24 rounded-lg border border-slate-300 px-3 py-1.5 text-sm num text-right focus:outline-none focus:ring-2 focus:ring-brand" /></Row>
          </div>
        </Card>
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4">College Pricing & Credits</div>
          <div className="space-y-3">
            {cfg.college_plans.filter((p) => p.type === "credits").map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-200 p-3">
                <div className="text-sm font-semibold text-ink mb-2">{p.name}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-ink-faint">Price</label><Money cur={cur} testid={`price-${p.id}`} value={p.price} onChange={(v) => setPlanField("college_plans", p.id, "price", parseInt(v || 0))} /></div>
                  <div><label className="text-xs text-ink-faint">Credits</label><input data-testid={`credits-${p.id}`} type="number" value={p.credits} onChange={(e) => setPlanField("college_plans", p.id, "credits", parseInt(e.target.value || 0))} className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand" /></div>
                </div>
              </div>
            ))}
            <Row label="Credit cost — AI"><input data-testid="cost-ai" type="number" value={cfg.credit_costs.ai} onChange={(e) => setCfg({ ...cfg, credit_costs: { ...cfg.credit_costs, ai: parseInt(e.target.value || 0) } })} className="w-24 rounded-lg border border-slate-300 px-3 py-1.5 text-sm num text-right focus:outline-none focus:ring-2 focus:ring-brand" /></Row>
            <Row label="Credit cost — Expert"><input data-testid="cost-expert" type="number" value={cfg.credit_costs.expert} onChange={(e) => setCfg({ ...cfg, credit_costs: { ...cfg.credit_costs, expert: parseInt(e.target.value || 0) } })} className="w-24 rounded-lg border border-slate-300 px-3 py-1.5 text-sm num text-right focus:outline-none focus:ring-2 focus:ring-brand" /></Row>
          </div>
        </Card>
      </div>

      {/* Manage colleges */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading font-bold text-ink flex items-center gap-2"><Building2 className="h-4 w-4 text-brand" /> Manage Colleges</div>
          <button data-testid="add-college" onClick={() => setModal(true)} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-4 py-2 rounded-full transition-colors"><Plus className="h-4 w-4" /> Onboard College</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200"><th className="px-3 py-2.5">College</th><th className="px-3 py-2.5">Admin</th><th className="px-3 py-2.5">Plan</th><th className="px-3 py-2.5">Students</th><th className="px-3 py-2.5">Credits</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5 text-right">Actions</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {colleges.map((c) => (
                <tr key={c.id} data-testid={`college-row-${c.code}`}>
                  <td className="px-3 py-3 font-semibold text-ink">{c.name}<div className="text-xs text-ink-faint num">{c.code}</div></td>
                  <td className="px-3 py-3 text-ink-soft text-xs">{c.admin_email}</td>
                  <td className="px-3 py-3 capitalize text-ink-soft">{c.plan || "—"}</td>
                  <td className="px-3 py-3 num text-ink-soft">{c.students}</td>
                  <td className="px-3 py-3 num text-ink-soft">{c.credits_used}/{c.credits_purchased}</td>
                  <td className="px-3 py-3"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{c.status}</span></td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`payment-link-${c.code}`} onClick={() => paymentLink(c)} className="p-2 rounded-lg hover:bg-slate-100 text-ink-soft" title="Send payment link"><Link2 className="h-4 w-4" /></button>
                      <button data-testid={`toggle-${c.code}`} onClick={() => toggleStatus(c)} className="p-2 rounded-lg hover:bg-slate-100 text-ink-soft" title="Toggle status"><Power className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3"><span className="text-sm text-ink-soft">{label}</span>{children}</div>
);
const Money = ({ cur, value, onChange, testid }) => (
  <div className="flex items-center gap-1"><span className="text-sm text-ink-faint num">{cur}</span><input data-testid={testid} type="number" value={value} onChange={(e) => onChange(e.target.value)} className="w-28 rounded-lg border border-slate-300 px-3 py-1.5 text-sm num text-right focus:outline-none focus:ring-2 focus:ring-brand" /></div>
);
